/**
 * usePushNotifications — mounted once in App, like useMechatUnread.
 *
 * Android-only (guarded by Capacitor.isNativePlatform()): registers this
 * device for FCM, then tells our own /api/push-register about EVERY saved
 * account on the device (not just the active one — money can arrive on any
 * of them while the app is closed). Re-syncs whenever the account list, an
 * account's label, or the app language changes, so a renamed account's
 * notifications pick up the new label without the user doing anything.
 *
 * Deep link on tap: pushNotificationActionPerformed's data carries
 * {account, type, from}. We can't safely switch accounts or navigate while
 * the session may be locked, so we just stash it in wallet.store's
 * `pendingPushTarget` — App.tsx / wallet.store.ts consume it once unlocked.
 *
 * Requires a real Firebase project wired into the Android build
 * (google-services.json + FIREBASE_SERVICE_ACCOUNT on the server side) —
 * see docs/LEARNINGS.md, 2026-09-07, for the exact setup steps. Without it,
 * requestPermissions()/register() simply fail silently and the app behaves
 * exactly as before.
 */

import { useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications, type Token, type PushNotificationSchema, type ActionPerformed } from '@capacitor/push-notifications'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { registerPushToken } from '../lib/pushApi'

const CHANNEL_ID = 'properwallet-events'

export function usePushNotifications(): void {
  const accounts = useWalletStore(s => s.accounts)
  const lang = useAppStore(s => s.lang)
  const setPendingPushTarget = useWalletStore(s => s.setPendingPushTarget)
  const tokenRef = useRef<string | null>(null)
  const accountsRef = useRef(accounts)
  const langRef = useRef(lang)
  accountsRef.current = accounts
  langRef.current = lang

  function syncAllAccounts() {
    const token = tokenRef.current
    if (!token) return
    for (const acc of accountsRef.current) {
      void registerPushToken({
        account: acc.name,
        token,
        label: acc.label?.trim() || acc.name,
        lang: langRef.current,
      })
    }
  }

  // Register the device + wire listeners once.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let cancelled = false

    void (async () => {
      try {
        await PushNotifications.createChannel({
          id: CHANNEL_ID,
          name: 'Пополнения и сообщения',
          description: 'Входящие переводы и новые сообщения чата',
          importance: 4, // HIGH — heads-up + tray icon + launcher badge
          visibility: 1,
        })
        const perm = await PushNotifications.checkPermissions()
        const granted =
          perm.receive === 'granted' ||
          (perm.receive === 'prompt' && (await PushNotifications.requestPermissions()).receive === 'granted')
        if (!granted || cancelled) return
        await PushNotifications.register()
      } catch {
        // No Firebase config yet, or a platform without Play Services — no-op.
      }
    })()

    const regSub = PushNotifications.addListener('registration', (token: Token) => {
      tokenRef.current = token.value
      syncAllAccounts()
    })
    const actionSub = PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      const data = action.notification.data as { account?: string; type?: string; from?: string } | undefined
      if (data?.account) {
        setPendingPushTarget({
          account: data.account,
          type: data.type === 'message' ? 'message' : 'transfer',
          from: data.from,
        })
      }
    })
    // Foreground receipt — the in-app pollers (useMechatUnread / balance
    // refresh) already cover the foreground case, nothing extra needed here.
    const receivedSub = PushNotifications.addListener('pushNotificationReceived', (_n: PushNotificationSchema) => {})

    return () => {
      cancelled = true
      void regSub.then(s => s.remove())
      void actionSub.then(s => s.remove())
      void receivedSub.then(s => s.remove())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-sync whenever the account list, a label, or the language changes.
  useEffect(() => {
    syncAllAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, lang])
}
